import React from 'react';

import { IconAutomation } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { SystemPlan } from '@cfa/pathway-fe-shared/xp';
import { Divider } from '@cfa/react-core';

import usePlanOwner from '@/hooks/usePlanOwner';

import { PathwayModal } from '@/components/popups/PathwayModal';

import { isPlanOwnerEligible } from '@/routes/TrainingPlans/isPlanOwnerEligible';

const DAYS_PER_YEAR = 365;

interface EnableCompliancePlanModalProps {
  plan: SystemPlan;
  isPending?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Enable Compliance Plan modal content.
 * Renders PathwayModal sub-components — must be placed inside a <PathwayModal> parent.
 */
export const EnableCompliancePlanModal: React.FC<
  EnableCompliancePlanModalProps
> = ({ plan, isPending = false, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const { shouldSelectOwner } = usePlanOwner({
    isEligible: isPlanOwnerEligible,
  });

  const getRecertificationPeriod = (): string => {
    const days = plan.expireAfterCompletionOffsetInDays;
    if (!days) {
      return t(
        'Settings.enableCompliancePlanModal.recertificationPeriod.oneYear',
      );
    }
    const years = Math.round(days / DAYS_PER_YEAR);
    return years >= 3
      ? t('Settings.enableCompliancePlanModal.recertificationPeriod.threeYears')
      : t('Settings.enableCompliancePlanModal.recertificationPeriod.oneYear');
  };

  return (
    <>
      <PathwayModal.Header>
        <PathwayModal.Title>
          {t('Settings.enableCompliancePlanModal.title')}
        </PathwayModal.Title>
      </PathwayModal.Header>
      <PathwayModal.Body noMinHeight>
        <Divider />
        <div className="text-grayscale-gray7 flex flex-col px-3 py-2 text-left text-base/6">
          <p>{t('Settings.enableCompliancePlanModal.intro')}</p>
          <ul className="mt-2 list-none space-y-2 pl-4">
            <li className="flex items-start gap-2.5">
              <IconAutomation
                className="text-grayscale-gray7 mt-1 shrink-0"
                size={16}
                stroke={1.5}
              />
              <span className="flex-1">
                {t('Settings.enableCompliancePlanModal.bullet1')}
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <IconAutomation
                className="text-grayscale-gray7 mt-1 shrink-0"
                size={16}
                stroke={1.5}
              />
              <span className="flex-1">
                {t('Settings.enableCompliancePlanModal.bullet2')}
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <IconAutomation
                className="text-grayscale-gray7 mt-1 shrink-0"
                size={16}
                stroke={1.5}
              />
              <span className="flex-1">
                {t('Settings.enableCompliancePlanModal.bullet3', {
                  period: getRecertificationPeriod(),
                })}
              </span>
            </li>
          </ul>
          <p className="mt-4">
            {shouldSelectOwner
              ? t('Settings.enableCompliancePlanModal.footerMultipleOperators')
              : t('Settings.enableCompliancePlanModal.footer')}
          </p>
        </div>
        <Divider />
      </PathwayModal.Body>
      <PathwayModal.Footer>
        <PathwayModal.SecondaryButton
          color="secondary"
          variant="outlined"
          onPress={onClose}
        >
          {t('Button.cancel')}
        </PathwayModal.SecondaryButton>
        <PathwayModal.PrimaryButton
          color="secondary"
          variant="filled"
          isPending={isPending}
          isDisabled={isPending}
          onPress={onConfirm}
        >
          {t('Button.enable')}
        </PathwayModal.PrimaryButton>
      </PathwayModal.Footer>
    </>
  );
};
